import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Pagination from 'common-ui/table/Pagination';
import {
  createOffer,
  deleteOffer,
  getOffers,
  updateOffer,
} from 'features/newsales/newsalesActions';
import {
  selectOffers,
  selectOffersCurrentPage,
  selectOffersDeleteSuccess,
  selectOffersLoading,
  selectOffersPagesCount,
  selectOffersSearchData,
  selectOffersSuccess,
  selectOffersUpdateSuccess,
  setOffersCreateSuccess,
  setOffersCurrentPage,
  setOffersDeleteSuccess,
  setOffersSearchData,
  setOffersUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import LeadSourceImage from '../components/LeadSourceImage';
import OfferCard from '../components/OfferCard';
import OfferModal from '../components/OfferModal';
import SearchFilter from '../components/SearchFilter';
import {
  CardsGrid,
  EmptyState,
  LoadingSpinner,
  OffersContainer,
  OffersContent,
  OffersTitle,
  PaginationWrapper,
} from './Offers.styles';

const Offers = () => {
  const dispatch = useDispatch();

  const offers = useSelector(selectOffers);
  const loading = useSelector(selectOffersLoading);
  const currentPage = useSelector(selectOffersCurrentPage);
  const pagesCount = useSelector(selectOffersPagesCount);
  const searchData = useSelector(selectOffersSearchData);
  const createSuccess = useSelector(selectOffersSuccess);
  const deleteSuccess = useSelector(selectOffersDeleteSuccess);
  const updateSuccess = useSelector(selectOffersUpdateSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedOffer, setSelectedOffer] = useState(null);

  const resetOffer = () => {
    dispatch(
      getOffers({
        status: 'enabled',
        page: 1,
        limit: 4,
      })
    );
  };

  useEffect(() => {
    dispatch(
      getOffers({
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        name: searchData.search,
      })
    );
  }, [dispatch, currentPage, searchData]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedOffer(null);
      dispatch(setOffersCreateSuccess(false));
      resetOffer();
    }
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedOffer(null);
      dispatch(setOffersUpdateSuccess(false));
      resetOffer();
    }
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setOffersDeleteSuccess(false));
      resetOffer();
    }
  }, [deleteSuccess, dispatch, offers?.length, currentPage, searchData]);

  const handleSearchChange = (value) => {
    dispatch(setOffersSearchData({ search: value }));
    dispatch(setOffersCurrentPage(1));
  };

  const handleStatusChange = (value) => {
    dispatch(setOffersSearchData({ status: value }));
    dispatch(setOffersCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setOffersCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setOffersSearchData({ limit: count }));
    dispatch(setOffersCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedOffer(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const offer = offers.find((o) => o.id === id);
    if (offer) {
      setModalMode('edit');
      setSelectedOffer(offer);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOffer(null);
  };

  const handleSubmit = (idOrData, offerData) => {
    if (modalMode === 'create') {
      dispatch(createOffer(idOrData));
    } else {
      dispatch(updateOffer({ id: idOrData, data: offerData }));
    }
  };

  const handleDeleteOffer = (id) => {
    dispatch(deleteOffer(id));
  };

  return (
    <OffersContainer>
      <OffersTitle>Offers</OffersTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search offers..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create Offer"
      />
      <OffersContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !offers?.length ? (
          <EmptyState>No offers found</EmptyState>
        ) : (
          <>
            <CardsGrid>
              {offers.map((offer) => {
                return (
                  <OfferCard
                    key={offer.id}
                    icon={
                      <LeadSourceImage imagePath={offer.image} alt={offer.name} size={40} />
                    }
                    title={offer.name}
                    product={offer.product?.name || offer.productName || ''}
                    status={offer.enabled ? 'Enabled' : 'Disabled'}
                    description={offer.description}
                    premiumFeatures={offer.premiumFeatures ?? []}
                    discountedPrice={offer.discountedPrice}
                    currentPrice={offer.price || offer.currentPrice}
                    count={offer.count ?? null}
                    onEdit={() => handleOpenEditModal(offer.id)}
                    onDelete={() => handleDeleteOffer(offer.id)}
                  />
                );
              })}
            </CardsGrid>

            {pagesCount > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={offers.length}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </OffersContent>

      <OfferModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedOffer}
      />
    </OffersContainer>
  );
};

export default Offers;
